import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60_000;

/**
 * v1.3.35.0 — Removal Queue notification bell.
 *
 * Renders a compact bell icon with a red count badge for
 * super_admin+ users. Polls `/api/removal-requests/pending-summary`
 * every 60s. Silent + zero-render for everyone else — the endpoint
 * itself returns `visible: false` for non-super-admins.
 *
 * Click → navigates to `/admin/removal-requests`.
 */
export default function RemovalQueueBell({ user, compact = false }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const timerRef = useRef(null);

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'founder';

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let cancelled = false;
    const fetchSummary = async () => {
      try {
        const { data } = await axios.get(`${API}/removal-requests/pending-summary`);
        if (!cancelled) setSummary(data);
      } catch {
        // Silent — bell hides on error.
        if (!cancelled) setSummary(null);
      }
    };
    fetchSummary();
    timerRef.current = window.setInterval(fetchSummary, POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [isSuperAdmin]);

  if (!isSuperAdmin || !summary?.visible) return null;
  const count = summary.total || 0;

  const label = count === 0
    ? 'No pending removal requests'
    : `${count} pending removal request${count === 1 ? '' : 's'} (${summary.facilities} facility · ${summary.clients} client)`;

  return (
    <button
      type="button"
      onClick={() => navigate('/admin/removal-requests')}
      title={label}
      data-testid="removal-queue-bell"
      className={`relative inline-flex items-center justify-center rounded-md text-slate-300 hover:text-white hover:bg-slate-800 transition ${compact ? 'w-9 h-9' : 'w-9 h-9'}`}
    >
      <Bell size={18} />
      {count > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center px-1 border border-slate-900"
          data-testid="removal-queue-bell-badge"
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}
